import React, { useState, useRef, useEffect } from 'react';
import type { BoostedUser } from '../../types/index';
import { Button } from '../ui/button'; // Assuming Button component is in ui folder
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Default maximum number of profiles to display in the carousel.
 * This limit provides optimal performance and user experience:
 * - Prevents UI clutter and overwhelming choice
 * - Reduces DOM elements for better performance
 * - Maintains smooth scrolling experience
 * - Encourages users to explore via "View More" button
 */
const DEFAULT_MAX_PROFILES = 10;

/**
 * Props for the PagesCarousel component.
 * 
 * @property {BoostedUser[]} profiles - An array of boosted user profiles.
 * @property {string} [title="Pages You Might Like"] - The title of the carousel.
 * @property {number} [maxProfiles=DEFAULT_MAX_PROFILES] - The maximum number of profiles to display.
 */
interface PagesCarouselProps {
  profiles: BoostedUser[];
  title?: string;
  maxProfiles?: number; // Maximum number of profiles to display (default: DEFAULT_MAX_PROFILES)
}

/**
 * A carousel component for displaying boosted user profiles.
 * 
 * The component is designed to be reusable and configurable. It accepts an array of user profiles,
 * a title, and an optional maximum number of profiles to display. The component uses a scrollable
 * container to display the profiles, with navigation buttons to scroll left and right.
 * 
 * The design decision to make the profile limit configurable was made to improve maintainability.
 * This allows the component to be easily reused in different contexts, where the number of profiles
 * to display may vary.
 */
const PagesCarousel: React.FC<PagesCarouselProps> = ({ profiles, title = "Pages You Might Like", maxProfiles = DEFAULT_MAX_PROFILES }) => {
  const navigate = useNavigate();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftButton, setShowLeftButton] = useState(false);
  const [showRightButton, setShowRightButton] = useState(true);
  
  if (!profiles || profiles.length === 0) {
    return null;
  }

  const handleViewMore = () => {
    navigate('/pages');
  };

  const handlePageClick = (pageId: string) => {
    navigate(`/pages/${pageId}`);
  };

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowLeftButton(scrollLeft > 0);
      setShowRightButton(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      // Dynamic scroll calculation for better maintainability
      const container = scrollContainerRef.current;
      const containerWidth = container.clientWidth;
      
      // Try to get actual item width from the first carousel item
      const firstItem = container.querySelector('[data-carousel-item]') as HTMLElement;
      const itemWidth = firstItem ? firstItem.offsetWidth : 220; // Fallback to 220px if item not found
      
      // Calculate optimal scroll amount: scroll approximately 2 items or 80% of visible width
      const itemBasedScroll = itemWidth * 2; // 2 items worth
      const containerBasedScroll = containerWidth * 0.8; // 80% of visible width
      
      // Use the smaller value to ensure we don't scroll too far
      const optimalScrollAmount = Math.min(itemBasedScroll, containerBasedScroll);
      
      const scrollAmount = direction === 'left' ? -optimalScrollAmount : optimalScrollAmount;
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      // Check if right button should be shown initially
      setShowRightButton(scrollContainer.scrollWidth > scrollContainer.clientWidth);
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, []);

  return (
    <div className="mb-4">
      <div className="flex justify-between items-center mb-2 px-4">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {title}
        </h3>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={handleViewMore} 
          className="text-xs text-gray-600 dark:text-gray-300 bg-transparent hover:bg-[#065900] hover:text-white active:bg-[#065900] active:text-white rounded-md transition-colors duration-200"
        >
          View More
        </Button>
      </div>
      <div className="relative">
        {showLeftButton && (
          <button 
            onClick={() => scroll('left')} 
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white dark:bg-gray-800 rounded-md p-1 shadow-md hover:bg-[#065900] active:bg-[#065900] dark:hover:bg-[#065900] dark:active:bg-[#065900] focus:outline-none focus:ring-2 focus:ring-[#065900] transition-colors duration-200 group"
            aria-label="Scroll left"
          >
            <ChevronLeft className="w-6 h-6 text-gray-700 dark:text-white group-hover:text-white" />
          </button>
        )}
        
        {showRightButton && (
          <button 
            onClick={() => scroll('right')} 
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white dark:bg-gray-800 rounded-md p-1 shadow-md hover:bg-[#065900] active:bg-[#065900] dark:hover:bg-[#065900] dark:active:bg-[#065900] focus:outline-none focus:ring-2 focus:ring-[#065900] transition-colors duration-200 group"
            aria-label="Scroll right"
          >
            <ChevronRight className="w-6 h-6 text-gray-700 dark:text-white group-hover:text-white" />
          </button>
        )}
        
        <div 
          ref={scrollContainerRef} 
          className="flex overflow-x-auto hide-scrollbar gap-3 px-4 py-2 scroll-smooth" 
          data-component-name="PagesCarousel"
        >
          {profiles.slice(0, Math.min(profiles.length, maxProfiles)).map(profile => (
          <div 
            key={profile.id} 
            className="min-w-[220px] max-w-[220px] cursor-pointer group" 
            onClick={() => handlePageClick(profile.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handlePageClick(profile.id);
              }
            }}
            data-carousel-item
          >
            <div className="aspect-[4/5] bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden relative shadow-sm hover:shadow-md transition-shadow duration-300">
              <img 
                src={profile.cover_url || profile.avatar_url || 'https://cdn.pixabay.com/photo/2017/08/30/01/05/milky-way-2695569_1280.jpg'} 
                alt={profile.fullName}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 min-h-80"
                data-component-name="PagesCarousel"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                <div className="flex items-center bg-black/40 backdrop-blur-sm p-2 rounded-lg" data-component-name="PagesCarousel">
                  <img 
                    src={profile.avatar_url || 'https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png'} 
                    alt={profile.username} 
                    className="w-8 h-8 rounded-full mr-2 border border-white/30"
                  />
                  <div>
                    <p className="text-sm font-medium truncate text-white">{profile.fullName}</p>
                    <p className="text-xs text-gray-300 truncate">@{profile.username}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
        </div>
      </div>
    </div>
  );
};

export default PagesCarousel;
